/* Reusable in-app image viewer + thumbnail cell.
   - ImageLightbox: full-size preview with prev/next carousel, keyboard arrows/Esc,
     and a Download button (fetches the blob; falls back to opening the URL if the
     host blocks cross-origin fetch).
   - ProductImageThumbs: a row of small clickable thumbnails that opens the lightbox
     in place — drop it straight into a DataGrid renderCell.
   Matches the panel's current look (navy/muted type, soft borders).
   NOTE: keep in sync with proship-backend/src/app/common/ImageLightbox.js. */
import { useCallback, useEffect, useState } from 'react';
import { Box, Button, CircularProgress, Dialog, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import {
    ChevronLeftOutlined, ChevronRightOutlined, CloseOutlined,
    FileDownloadOutlined, ImageNotSupportedOutlined, ZoomOutMapOutlined,
} from '@mui/icons-material';

const BORDER = '#E7EAF0';
const FONT = 'Poppins, sans-serif';

// Canonical image field is `images` (array up to 5); older rows used image1..image5.
export const imagesOf = (row) =>
    (Array.isArray(row?.images) ? row.images : [row?.image1, row?.image2, row?.image3, row?.image4, row?.image5]).filter(Boolean);

// Best-effort filename from an S3/CloudFront URL — legacy rows encode the path
// oddly (…proship-uploads%5C/name.jpg%3Fv%3D…), so split on / \ and the query too.
export const fileNameFromUrl = (url) => {
    try {
        const clean = String(url).split('?')[0].split('%3F')[0];
        const parts = clean.split(/[/\\]|%5C/i);
        return decodeURIComponent(parts[parts.length - 1] || '') || 'image.jpg';
    } catch {
        return 'image.jpg';
    }
};

// Square image tile with graceful fallback (some legacy URLs may 404). Clicking
// calls onOpen (opens the lightbox) instead of navigating away.
export function Thumb({ src, size = 34, radius = 6, onOpen }) {
    const [err, setErr] = useState(false);
    if (!src || err) {
        return (
            <Box sx={{
                width: size, height: size, borderRadius: `${radius}px`, border: `1px dashed ${BORDER}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#F8FAFC', flexShrink: 0,
            }}>
                <ImageNotSupportedOutlined sx={{ fontSize: size * 0.42, color: '#C4CDD5' }} />
            </Box>
        );
    }
    return (
        <Box role="button" title="View image" onClick={onOpen}
            sx={{
                position: 'relative', display: 'block', lineHeight: 0, flexShrink: 0, cursor: 'pointer', borderRadius: `${radius}px`,
                transition: 'transform .15s ease, box-shadow .15s ease',
                '&:hover': { transform: 'scale(1.12)', zIndex: 2, boxShadow: '0 4px 12px rgba(15,23,42,0.20)' },
                '&:hover .thumb-zoom': { opacity: 1 }, '&:hover img': { filter: 'brightness(0.92)' },
            }}>
            <img src={src} alt="" onError={() => setErr(true)}
                style={{ width: size, height: size, objectFit: 'cover', borderRadius: radius, border: `1px solid ${BORDER}`, display: 'block' }} />
            {size >= 40 && (
                <ZoomOutMapOutlined className="thumb-zoom" sx={{
                    position: 'absolute', bottom: 3, right: 3, fontSize: 14, color: '#fff', p: '2px',
                    bgcolor: 'rgba(0,0,0,0.5)', borderRadius: '4px', opacity: 0, transition: 'opacity .15s ease',
                }} />
            )}
        </Box>
    );
}

export function ImageLightbox({ images, index = 0, onClose }) {
    const list = (images || []).filter(Boolean);
    const count = list.length;
    const [idx, setIdx] = useState(Math.min(Math.max(index, 0), Math.max(count - 1, 0)));
    const [downloading, setDownloading] = useState(false);

    const prev = useCallback(() => setIdx((i) => (i - 1 + count) % count), [count]);
    const next = useCallback(() => setIdx((i) => (i + 1) % count), [count]);

    useEffect(() => {
        const onKey = (e) => {
            if (e.key === 'ArrowLeft') prev();
            else if (e.key === 'ArrowRight') next();
            else if (e.key === 'Escape') onClose();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [prev, next, onClose]);

    const current = list[idx];

    const download = async () => {
        if (!current) return;
        setDownloading(true);
        try {
            const res = await fetch(current);
            const blob = await res.blob();
            const objectUrl = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = objectUrl;
            a.download = fileNameFromUrl(current);
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(objectUrl);
        } catch {
            window.open(current, '_blank', 'noopener'); // cross-origin fetch blocked — open instead
        } finally {
            setDownloading(false);
        }
    };

    if (!current) return null;
    const navBtnSx = { color: '#fff', bgcolor: 'rgba(255,255,255,0.12)', '&:hover': { bgcolor: 'rgba(255,255,255,0.24)' } };

    return (
        <Dialog open onClose={onClose} maxWidth="md" fullWidth
            PaperProps={{ sx: { borderRadius: '14px', bgcolor: '#0f172a', backgroundImage: 'none' } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.25 }}>
                <Typography sx={{ color: 'rgba(255,255,255,0.72)', fontSize: 13, fontWeight: 600, fontFamily: FONT }}>
                    {count > 1 ? `${idx + 1} / ${count}` : 'Image'}
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                    <Button onClick={download} disabled={downloading} variant="contained" disableElevation size="small"
                        startIcon={downloading ? <CircularProgress size={15} sx={{ color: '#fff' }} /> : <FileDownloadOutlined />}
                        sx={{ textTransform: 'none', fontWeight: 700, bgcolor: '#1565c0', '&:hover': { bgcolor: '#0d47a1' } }}>
                        Download
                    </Button>
                    <Tooltip title="Close">
                        <IconButton onClick={onClose} sx={{ color: '#fff' }}><CloseOutlined /></IconButton>
                    </Tooltip>
                </Stack>
            </Box>

            <Box sx={{ position: 'relative', px: 2, pb: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 320 }}>
                {count > 1 && (
                    <IconButton onClick={prev} sx={{ ...navBtnSx, position: 'absolute', left: 12 }}><ChevronLeftOutlined /></IconButton>
                )}
                <Box component="img" src={current} alt={fileNameFromUrl(current)}
                    sx={{ maxWidth: '100%', maxHeight: '68vh', objectFit: 'contain', borderRadius: '8px', display: 'block' }} />
                {count > 1 && (
                    <IconButton onClick={next} sx={{ ...navBtnSx, position: 'absolute', right: 12 }}><ChevronRightOutlined /></IconButton>
                )}
            </Box>

            {count > 1 && (
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap', px: 2, pb: 2 }}>
                    {list.map((u, i) => (
                        <Box key={i} component="img" src={u} alt="" onClick={() => setIdx(i)}
                            sx={{
                                width: 46, height: 46, objectFit: 'cover', borderRadius: '6px', cursor: 'pointer',
                                border: i === idx ? '2px solid #1565c0' : '2px solid transparent',
                                opacity: i === idx ? 1 : 0.55, transition: 'opacity .15s ease', '&:hover': { opacity: 1 },
                            }} />
                    ))}
                </Box>
            )}
        </Dialog>
    );
}

// Grid-cell friendly: a row of clickable thumbnails that opens the lightbox in
// place. Shows at most `max` thumbnails; the rest collapse into a "+N" tile that
// opens the carousel at the first hidden image.
export function ProductImageThumbs({ images, size = 34, max = 2, empty = 'No image' }) {
    const list = (images || []).filter(Boolean);
    const [lb, setLb] = useState(null); // start index, or null
    if (!list.length) {
        return (
            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ py: 0.5 }}>
                <Box sx={{
                    width: size, height: size, borderRadius: '6px', border: `1px dashed ${BORDER}`, flexShrink: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#F8FAFC',
                }}>
                    <ImageNotSupportedOutlined sx={{ fontSize: size * 0.5, color: '#C4CDD5' }} />
                </Box>
                <Typography sx={{ fontSize: 11.5, color: '#8A94A6', fontFamily: FONT }}>{empty}</Typography>
            </Stack>
        );
    }
    return (
        <>
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ py: 0.5 }}>
                {list.slice(0, max).map((u, i) => <Thumb key={i} src={u} size={size} onOpen={() => setLb(i)} />)}
                {list.length > max && (
                    <Box onClick={() => setLb(max)} title={`View all ${list.length} images`}
                        sx={{
                            width: size, height: size, borderRadius: '6px', border: `1px solid ${BORDER}`, cursor: 'pointer',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#F1F5F9',
                            fontSize: 11.5, fontWeight: 700, color: '#475569', fontFamily: FONT, flexShrink: 0,
                            '&:hover': { bgcolor: '#E2E8F0' },
                        }}>
                        +{list.length - max}
                    </Box>
                )}
            </Stack>
            {lb !== null && <ImageLightbox images={list} index={lb} onClose={() => setLb(null)} />}
        </>
    );
}
